import { Badge, Button } from '@iptv/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Trash2, Upload, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { adminFetch, type ApiError } from '../lib/api';
import {
  inferSlot,
  REQUIRED_SLOTS,
  REQUIRED_SLOT_COUNT,
  type RequiredSlot,
} from '../lib/required-slots';

interface AssetRow {
  id: number;
  slot: string;
  site_slug: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  is_active: boolean;
  uploaded_by: number | null;
  created_at: string;
  updated_at: string;
  url: string;
}

interface AssetListResponse {
  items: AssetRow[];
  total: number;
  coverage: { uploaded: number };
}

interface UploadJob {
  file: File;
  slot: string;
  status: 'queued' | 'uploading' | 'done' | 'error';
  message?: string;
}

const SITE_SLUG = 'wc-en'; // single-site for the launch — easy to expand later

const CATEGORIES: Array<RequiredSlot['category']> = [
  'hero',
  'cities',
  'classics',
  'wc-rail',
  'docs',
  'vod',
  'broadcasters',
  'channels',
  'testimonials',
];

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function AssetsPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [filter, setFilter] = useState<RequiredSlot['category'] | 'all'>('all');
  const [uploadJobs, setUploadJobs] = useState<UploadJob[]>([]);

  const list = useQuery({
    queryKey: ['admin', 'assets', SITE_SLUG],
    queryFn: () =>
      adminFetch<AssetListResponse>('/api/v1/admin/assets', {
        query: { site_slug: SITE_SLUG, active_only: true, limit: 500 },
      }),
  });

  const activeBySlot = useMemo(() => {
    const map = new Map<string, AssetRow>();
    for (const a of list.data?.items ?? []) {
      if (a.is_active) map.set(a.slot, a);
    }
    return map;
  }, [list.data]);

  const slotsByCategory = useMemo(() => {
    const grouped: Record<string, RequiredSlot[]> = {};
    for (const s of REQUIRED_SLOTS) {
      (grouped[s.category] ??= []).push(s);
    }
    return grouped;
  }, []);

  const uploaded = activeBySlot.size;
  const required = REQUIRED_SLOT_COUNT;
  const missing = Math.max(0, required - uploaded);
  const pct = Math.round((uploaded / required) * 100);

  const upload = useMutation({
    mutationFn: async ({ file, slot }: { file: File; slot: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('slot', slot);
      fd.append('site_slug', SITE_SLUG);
      return adminFetch<AssetRow>('/api/v1/admin/assets', {
        method: 'POST',
        formData: fd,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'assets'] }),
  });

  const remove = useMutation({
    mutationFn: (assetId: number) =>
      adminFetch<{ ok: boolean }>(`/api/v1/admin/assets/${assetId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'assets'] }),
  });

  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    const jobs: UploadJob[] = Array.from(files).map((file) => {
      const inferred = inferSlot(file.name);
      return {
        file,
        slot: inferred ?? '',
        status: inferred ? 'queued' : 'error',
        message: inferred
          ? undefined
          : `unrecognised filename — must match one of the ${REQUIRED_SLOT_COUNT} canonical slots (lowercase, exact)`,
      };
    });
    setUploadJobs(jobs);

    for (let i = 0; i < jobs.length; i += 1) {
      const job = jobs[i]!;
      if (!job.slot) continue;
      setUploadJobs((prev) =>
        prev.map((j, idx) => (idx === i ? { ...j, status: 'uploading' } : j)),
      );
      try {
        await upload.mutateAsync({ file: job.file, slot: job.slot });
        setUploadJobs((prev) => prev.map((j, idx) => (idx === i ? { ...j, status: 'done' } : j)));
      } catch (err) {
        const detail = (err as ApiError)?.detail ?? 'upload failed';
        setUploadJobs((prev) =>
          prev.map((j, idx) => (idx === i ? { ...j, status: 'error', message: detail } : j)),
        );
      }
    }
  }

  const visibleSlots: RequiredSlot[] = useMemo(() => {
    if (filter === 'all') return REQUIRED_SLOTS;
    return REQUIRED_SLOTS.filter((s) => s.category === filter);
  }, [filter]);

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Assets</h1>
          <p className="text-sm text-white/60">
            Drag &amp; drop image files. The slot is auto-inferred from the filename — see the
            canonical list below.
          </p>
        </div>
        <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2 text-sm">
          <span className="font-bold text-white">{uploaded}</span>
          <span className="text-white/60">/ {required} uploaded</span>
          <div className="h-2 w-32 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400"
              style={{ width: `${pct}%` }}
            />
          </div>
          {missing > 0 && <Badge variant="warning">{missing} missing</Badge>}
          {missing === 0 && <Badge variant="success">All set</Badge>}
        </div>
      </header>

      {/* Drag-drop zone — role=button + keyboard handler for a11y */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Click or drop files to upload assets"
        className="cursor-pointer rounded-xl border-2 border-dashed border-white/20 bg-white/[0.02] p-8 text-center transition hover:border-white/40 focus:border-white/60 focus:outline-none focus:ring-2 focus:ring-white/20"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDrop={(e) => {
          e.preventDefault();
          void handleFiles(e.dataTransfer.files);
        }}
      >
        <Upload className="mx-auto h-8 w-8 text-white/40" />
        <p className="mt-3 text-sm text-white/85">
          Drop files here or <span className="underline">browse</span>
        </p>
        <p className="mt-1 text-xs text-white/55">
          PNG, JPEG, WebP, SVG · max 5 MB · filename = slot name
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {/* Upload progress queue */}
      {uploadJobs.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-white/55">
              Upload queue ({uploadJobs.filter((j) => j.status === 'done').length} /{' '}
              {uploadJobs.length})
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setUploadJobs([])}
              disabled={uploadJobs.some((j) => j.status === 'uploading')}
            >
              clear
            </Button>
          </div>
          <ul className="space-y-1 text-xs">
            {uploadJobs.map((j, i) => (
              <li key={i} className="flex items-center justify-between gap-2">
                <span className="truncate font-mono text-white/85">{j.file.name}</span>
                <span className="flex items-center gap-2">
                  {j.slot && <code className="rounded bg-white/10 px-1.5 py-0.5">{j.slot}</code>}
                  {j.status === 'uploading' && <span className="text-white/55">uploading…</span>}
                  {j.status === 'done' && (
                    <span className="inline-flex items-center gap-1 text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5" /> done
                    </span>
                  )}
                  {j.status === 'error' && (
                    <span
                      className="inline-flex items-center gap-1 text-rose-400"
                      title={j.message}
                    >
                      <X className="h-3.5 w-3.5" /> {j.message ?? 'error'}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Category filter */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilter('all')}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
            filter === 'all'
              ? 'bg-white/15 text-white'
              : 'bg-white/5 text-white/60 hover:bg-white/10'
          }`}
        >
          All ({REQUIRED_SLOT_COUNT})
        </button>
        {CATEGORIES.map((cat) => {
          const count = (slotsByCategory[cat] ?? []).length;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setFilter(cat)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                filter === cat
                  ? 'bg-white/15 text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              {cat} ({count})
            </button>
          );
        })}
      </div>

      {/* Slot grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {visibleSlots.map((s) => {
          const asset = activeBySlot.get(s.slot);
          return (
            <div
              key={s.slot}
              className={`rounded-lg border p-3 transition ${
                asset ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/10 bg-white/[0.02]'
              }`}
            >
              <div className="aspect-video overflow-hidden rounded bg-black/40">
                {asset ? (
                  <img src={asset.url} alt={s.slot} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-[10px] uppercase tracking-wider text-white/30">
                    awaiting upload
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <code className="block truncate font-mono text-[11px] text-white/85">
                    {s.slot}
                  </code>
                  <span className="text-[10px] text-white/45">
                    {s.category} · {s.aspect}
                  </span>
                </div>
                {asset && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Soft-delete ${s.slot}?`)) {
                        void remove.mutateAsync(asset.id);
                      }
                    }}
                    className="text-rose-400/75 transition hover:text-rose-400"
                    title="Soft delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {asset && (
                <div className="mt-1 text-[10px] text-white/45">
                  {fmtBytes(asset.size_bytes)} · {asset.mime_type.split('/')[1]}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
